import moment from "moment"

import { images } from "./image-definitions"
import { organisationRoles, personRoles } from "./application-definitions"

const {
    placeholderVideoImage,
    placeholderPodcastImage,
    placeholderReportImage
} = images

/*
Format a provided timestamp for display.
Default format in most places is 'llll'.
*/
export function formatTimestamp(timestamp: number, format: string): moment {
    return moment.unix(timestamp).format(format || "llll")
}

/*
  Create a fake <a> tag, set its link to a URL
  generated from the received file response and then
  "click" it to transfer it (download) to the user.
*/
export function downloadFile(response: Object, fileName: string): void {
    // IE does not support "download" attributes on <a> tags,
    // so use msSaveBlob function where that is supported
    if (navigator.msSaveBlob) {
        navigator.msSaveBlob(response, fileName)
    } else {
        const aTag = document.createElement("a")
        aTag.href = URL.createObjectURL(response)
        aTag.download = fileName
        aTag.style.display = "none"
        document.body.appendChild(aTag)
        aTag.click()
        // Clean up the evidence of our fake <a> *shifty eyes*
        document.body.removeChild(aTag)
    }
}

export function getProviderTypeFromLabel(label: string): string {
    return label === "Organization" ? "PartnerEntity" : label
}

/*
  Capitalises each word of a string.
*/
export function capitaliseEachWord(str: string): string {
    return str.replace(/\w\S*/g, txt => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    })
}

/*
  Convert presentation accessor to readable strings, e.g. for no search result
  messages.
*/
export function getReadableEntityName(
    entityName: string,
    isPlural?: boolean
): string {
    switch (entityName) {
        case "sources":
            return isPlural ? "Sources" : "Source"
        case "insightAreas":
            return isPlural ? "Insight Areas" : "Insight Area"
        case "keyIssues":
            return isPlural ? "Key Issues" : "Key Issue"
        case "communities":
            return isPlural ? "Communities" : "Community"
        case "users":
            return isPlural ? "Users" : "User"
        default:
            return isPlural ? "results" : "result"
    }
}

// A functional copy of python's range function.
// Takes in a number and converts it to an array of [0..n-1]
export function range(upperLimit: number): Array<number> {
    const list = []
    for (let i = 0; i < upperLimit; i++) {
        list.push(i)
    }
    return list
}

export function randomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min)) + min
}

const hexValues = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "a",
    "b",
    "c",
    "d",
    "e",
    "f"
]

export function randomGrey(
    min1: number = 0,
    max1: number = 17,
    min2: number = 0,
    max2: number = 17
): string {
    if (max1 > 17 || max2 > 17) {
        throw new Error(`max is higher than 17 (${max1}, ${max2})`)
    }
    if (min1 < 0 || min2 < 0) {
        throw new Error(`min is lower than 0 (${min1}, ${max2})`)
    }
    const firstNum = randomInt(min1, max1)
    const secondNum = randomInt(min2, max2)
    const firstCharacter = hexValues[firstNum - 1]
    const secondCharacter = hexValues[secondNum - 2]
    const colour = range(3)
        .map(_ => `${firstCharacter}${secondCharacter}`)
        .reduce((colour, part) => colour + part, "#")
    return colour
}

export function validateUrl(url: string): boolean {
    // courtesy of https://someweblog.com/url-regular-expression-javascript-link-shortener/
    let regex = /\(?(?:(http|https|ftp):\/\/)?(?:((?:[^\W\s]|\.|-|[:]{1})+)@{1})?((?:www.)?(?:[^\W\s]|\.|-)+[.][^\W\s]{2,4}|localhost(?=\/)|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::(\d*))?([/]?[^\s?]*[/]{1})*(?:\/?([^\s\n?[\]{}#]*(?:(?=\.)){1}|[^\s\n?[]{\}\.#]*)?([.]{1}[^\s?#]*)?)?(?:\?{1}([^\s\n#[\]]*))?([#][^\s\n]*)?\)?/gi

    return regex.test(url)
}

export function forceArray(potentialArray: any): Array<any> {
    if (!potentialArray) {
        return []
    }
    if (!Array.isArray(potentialArray)) {
        return [potentialArray]
    }
    return potentialArray
}

export function getAttachmentPlaceholderImage(
    type: string,
    showPlaceholderOnError?: boolean = false
): ?string {
    switch (type) {
        case "video":
            return placeholderVideoImage
        case "podcast":
            return placeholderPodcastImage
        case "report":
            return showPlaceholderOnError ? placeholderReportImage : undefined
        default:
            return undefined
    }
}

export function arrayUnion(
    arr1: Array<string>,
    arr2: Array<string>
): Array<string> {
    const joinArray = [...arr1, ...arr2]

    return joinArray.filter((item, index) => joinArray.indexOf(item) === index)
}

export function getRoleOptions(stakeholderType: string): Array<string> {
    return stakeholderType === "Person" ? personRoles : organisationRoles
}
